import React from 'react';
import '../styles/App.css';
import '../styles/responsive.css';
import SmallFooter from '../components/SmallFooter';
import BrandLink from '../components/BrandLink';
import { LINKS } from '../constants';

function PrivacyPolicy() {
  return (
	<main>
	  <DefaultSection />
	</main>
  );
}

const DefaultSection = () => {
  return (
	<>
	  <section className="legal-page">
		<div className="legal-container">
		  <h1>Privacy Policy</h1>
		  <p className="legal-effective">Effective Date: May 2026</p>
		  <div>

			<p>
			  Barista Bot is a product of{' '}
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>{' '}
			  ("we," "us," or "our"). This page covers baristabot.app and the
			  SMS ordering service (the "Service"). The company-wide policy is
			  the{' '}
			  <BrandLink href={LINKS.espressoPrivacy}>
				Espresso + Milk Privacy Policy
			  </BrandLink>
			  .
			</p>

			<h2>Information We Collect</h2>
			<ul>
			  <li>
				<strong>Phone number</strong> — used to send one-time login
				codes and order confirmations via SMS.
			  </li>
			  <li>
				<strong>Name and email</strong> — optionally provided during
				account setup.
			  </li>
			  <li>
				<strong>Order history</strong> — beverage orders placed through
				our Service.
			  </li>
			  <li>
				<strong>Payment information</strong> — processed securely
				through Square. We do not store full card numbers; we may
				retain a last-four digit reference and expiration for receipts
				and dispute resolution.
			  </li>
			  <li>
				<strong>Device and log data</strong> — limited technical data
				such as IP address, browser type, and timestamps, used to
				operate and secure the Service.
			  </li>
			</ul>

			<h2>How We Use Your Information</h2>
			<ul>
			  <li>To authenticate your identity via SMS one-time codes.</li>
			  <li>To process and fulfill your orders.</li>
			  <li>To send order updates and support messages via SMS.</li>
			  <li>To detect, prevent, and respond to fraud or abuse.</li>
			  <li>To improve our Service and customer experience.</li>
			  <li>To comply with legal, tax, and accounting obligations.</li>
			</ul>

			<h2>SMS Messaging</h2>
			<p>
			  By providing your phone number and opting in, you consent to
			  receive SMS messages from Barista Bot, including:
			</p>
			<ul>
			  <li>
				<strong>Transactional messages</strong> — one-time login codes,
				order confirmations, pickup updates, and support replies.
			  </li>
			  <li>
				<strong>Promotional messages</strong> — including "Drops" from
				participating coffee shops featuring new coffees and offers,
				where you have opted in.
			  </li>
			</ul>
			<p>
			  Message frequency varies based on your activity and the shops
			  you follow. Message and data rates may apply. Reply{' '}
			  <strong>STOP</strong> at any time to opt out of all non-essential
			  messages, or <strong>HELP</strong> for assistance. Carriers are
			  not liable for delayed or undelivered messages. Opting out of
			  promotional messages will not affect transactional messages
			  required to operate your account and orders.
			</p>
			<p>
			  <strong>
				No mobile information will be shared with third parties or
				affiliates for marketing or promotional purposes. All
				categories of information listed in this Privacy Policy exclude
				text messaging originator opt-in data and consent; this
				information will not be shared with any third parties.
			  </strong>
			</p>
			<p>
			  We use Twilio as our SMS delivery provider strictly to transmit
			  messages to and from your device. Twilio's processing of message
			  metadata is governed by{' '}
			  <a
				href="https://www.twilio.com/legal/privacy"
				target="_blank"
				rel="noopener noreferrer"
			  >
				Twilio's privacy policy
			  </a>
			  .
			</p>

			<h2>Cookies and Analytics</h2>
			<p>
			  We use a minimal number of first-party cookies and local storage
			  entries strictly necessary to keep you logged in and to remember
			  your preferences. We do not use third-party advertising cookies.
			  Aggregated, non-identifying analytics may be used to understand
			  Service performance.
			</p>

			<h2>Data Sharing</h2>
			<p>
			  We do not sell or rent your personal information. We share data
			  only with service providers necessary to operate our platform,
			  including:
			</p>
			<ul>
			  <li><strong>Square</strong> — payment processing.</li>
			  <li><strong>Twilio</strong> — SMS delivery.</li>
			  <li><strong>Google Cloud</strong> — hosting and infrastructure.</li>
			  <li>
				<strong>Participating coffee shops</strong> — receive the order
				details necessary to prepare and fulfill your order.
			  </li>
			</ul>
			<p>
			  We may also disclose information when required by law, to enforce
			  our Terms of Service, or to protect the rights, property, or
			  safety of our users or others.
			</p>
			<p>
			  <strong>SMS opt-in carve-out:</strong> Your mobile phone number,
			  SMS opt-in status, and consent records are never shared with
			  third parties or affiliates for marketing or promotional
			  purposes, and are never sold.
			</p>

			<h2>Data Security</h2>
			<p>
			  We use industry-standard administrative, technical, and physical
			  safeguards designed to protect your information, including
			  encryption in transit and access controls. No method of
			  transmission or storage is 100% secure, and we cannot guarantee
			  absolute security.
			</p>

			<h2>Data Retention</h2>
			<p>
			  We retain your account information as long as your account is
			  active, and for a reasonable period thereafter to comply with
			  legal, tax, accounting, and dispute-resolution obligations. You
			  may request deletion by emailing{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a>.
			</p>

			<h2>Children's Privacy</h2>
			<p>
			  The Service is not directed to children under 13, and we do not
			  knowingly collect personal information from children under 13. If
			  you believe a child has provided us with information, contact us
			  and we will delete it.
			</p>

			<h2>Your California Privacy Rights</h2>
			<p>
			  If you are a California resident, you have the right to: (a) know
			  what personal information we collect about you; (b) request
			  deletion of your personal information; (c) correct inaccurate
			  personal information; and (d) opt out of any "sale" or "sharing"
			  of personal information. We do not sell personal information. To
			  exercise these rights, contact us at{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a>. We will
			  not discriminate against you for exercising these rights.
			</p>

			<h2>Changes to This Policy</h2>
			<p>
			  We may update this Privacy Policy from time to time. Material
			  changes will be communicated via SMS, email, or notice in the
			  customer portal. The "Effective Date" at the top reflects the
			  most recent revision.
			</p>

			<h2>Contact</h2>
			<p>
			  Questions or requests? Email us at{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a>.
			</p>
			<p>
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>
			  <br />
			  219 NW 12th St, Suite 23<br />
			  Oklahoma City, OK 73103
			</p>
		  </div>
		</div>
	  </section>
	  <SmallFooter />
	</>
  );
};

export default PrivacyPolicy;
